import classNames from 'classnames';
import isNaN from 'lodash/isNaN';
import isNumber from 'lodash/isNumber';
import React, { memo, useCallback, useMemo, useState } from 'react';
import MaskedInput from 'react-text-mask';
import { UiHoverTooltip } from 'src/components/UI/HoverTooltip';
import { useCleanHoverErrors } from 'src/components/UI/inputs/useHoverErrors';

import { useInputCleanBaseHook } from 'src/utils/hooks/inputs';
import { StateFormInputOptionsType, StateFormReturnType } from 'src/utils/stateForm';
import { getTestId } from 'src/utils/testing';
import { InputMaterialWrapper } from 'src/components/UI/inputs/common/InputMaterialWrapper';

export const maskedInputConvertMask = (mask: string): (RegExp | string)[] =>
  mask.split('').map((symbol) => {
    if (symbol === '9') {
      return /\d/;
    }

    if (symbol !== ' ' && !isNaN(+symbol) && isNumber(+symbol)) {
      return new RegExp(`[0-${symbol}]`);
    }

    return symbol;
  });

export type UiCleanMaskedInputProps = {
  formProps: StateFormReturnType;
  name: string;
  mask: string; // mask as a string

  className?: string;
  placeholder?: string;
  required?: boolean;
  requiredMessage?: string;
  errorLabel?: string;
  // the function to validate value (not blocking an input)
  validate?: StateFormInputOptionsType['validate'];
  disabled?: boolean;
  showHoverError?: boolean;
  dataTestId?: string;
  material?: boolean;
  clearOnBlurIfNotFullFilled?: boolean;
  defaultPlaceholderStyle?: boolean;
};

export const UiCleanMaskedInput = memo<UiCleanMaskedInputProps>(
  ({
    formProps: { clearErrors, getValue, onChange, onBlur, register, unregister, getSubscribeProps, setValue },
    name,
    className,
    required,
    requiredMessage,
    errorLabel,
    validate,
    placeholder,
    mask,
    disabled,
    showHoverError,
    dataTestId,
    material,
    clearOnBlurIfNotFullFilled,
    defaultPlaceholderStyle,
  }) => {
    const [active, setActive] = useState(false);

    const inputOptions = useMemo(
      (): StateFormInputOptionsType => ({
        required: required || false,
        requiredMessage,
        errorLabel,
        validate,
      }),
      [errorLabel, required, requiredMessage, validate]
    );

    const [value, errors] = useInputCleanBaseHook<string | number | undefined>({
      getSubscribeProps,
      name,
      inputOptions,
      unregister,
      register,
      type: 'masked',
    });
    const hasErrors = !!errors?.length;

    const arrMask = useMemo(() => maskedInputConvertMask(mask), [mask]);

    const { errorString, onChangeInputHover } = useCleanHoverErrors({
      name,
      errors,
      clearErrors,
      showAnyError: showHoverError,
    });

    const onChangeLocal = useCallback(
      ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
        if (disabled) {
          return;
        }
        onChange(name, value);

        onChangeInputHover();
      },
      [disabled, name, onChange, onChangeInputHover]
    );

    const onBlurLocal = useCallback(() => {
      onBlur(name);

      if (clearOnBlurIfNotFullFilled) {
        if ((getValue(name) || '').includes('_')) {
          setValue(name, null);
        }
      }

      if (!disabled && material) {
        setActive(false);
      }
    }, [clearOnBlurIfNotFullFilled, disabled, getValue, material, name, onBlur, setValue]);

    const handleFocus = useCallback(() => {
      if (!disabled) {
        setActive(true);
      }
    }, [disabled]);

    if (material) {
      return (
        <InputMaterialWrapper
          active={active}
          disabled={disabled}
          hasErrors={hasErrors}
          errorMessage={errorString}
          placeholder={defaultPlaceholderStyle ? undefined : placeholder}
          hasValue={!!value}
        >
          <MaskedInput
            mask={arrMask}
            type="text"
            disabled={disabled}
            className={classNames(className, {
              error: hasErrors,
            })}
            onChange={onChangeLocal}
            value={value}
            onBlur={onBlurLocal}
            data-testid={dataTestId && getTestId('input_masked', dataTestId, name)()}
            data-name={name}
            onFocus={handleFocus}
            placeholder={defaultPlaceholderStyle ? placeholder : undefined}
          />
        </InputMaterialWrapper>
      );
    }

    return (
      <UiHoverTooltip id={name} text={errorString} render={!!errorString} tooltipClassName="error-tooltip" noWrapper>
        <MaskedInput
          mask={arrMask}
          type="text"
          disabled={disabled}
          className={classNames(className, {
            error: hasErrors,
          })}
          placeholder={placeholder}
          onChange={onChangeLocal}
          value={value}
          onBlur={onBlurLocal}
          data-testid={dataTestId && getTestId('input_masked', dataTestId, name)()}
          data-name={name}
        />
      </UiHoverTooltip>
    );
  }
);
